import { sort } from 'timsort'
import crop from '../common/crop'
import { DataEvent } from '../../types'

type Span = {
    normalized?: ''
    text?: string
}

type Info = {
    span?: Span | string
    _span?: Span | string
}

type InfoEvent = {
    info: Info | string
}

type SpanEvent = {
    begin: number
    end: number
    type?: string
}

interface Message {
    // eslint-disable-next-line camelcase
    index_timestamp: number
    value: string
}

type State = {
    start: InfoEvent
    span: SpanEvent
    extended: number
    type: 'time' | 'depth'
    _span?: any
    buffer?: Message[]
    items: Message[]
}

type ListState = State & {
    items: any[]
}

type MessageEvent = (
    | {
          type: 'start'
          events: {
              info: { extended?: number; span: string; _span: { start: number; end: number } }
          }
      }
    | { type: 'span'; events: { begin: number; end: number } }
    | { type: 'endHistory' }
    | { type: 'event'; events: { timestamp: number; value: string }[] }
) & { baseline: boolean; layer: number }

function cropMessages(state: State): State {
    // remove based on span.
    // NOTE: state.span.end has extended on 1h to keep future events.
    if (state.span) {
        state.items = crop(state.items, state.span.begin, state.span.end + state.extended)
    }

    return state
}

export default function messageReducer(
    state: ListState = {
        start: {
            info: {
                span: { normalized: '' }
            }
        },
        type: 'time',
        extended: 0,
        items: [],
        span: { begin: 0, end: 0 }
    },
    event: MessageEvent
): ListState {
    if (event?.layer > 0) return state

    switch (event?.type) {
        case 'start':
            if (event.baseline) return state
            const originalSpan = event.events.info._span

            return {
                ...state,
                extended: event.events.info.extended ?? 0,
                // depth has a @meta _span with the user original span.
                type: originalSpan ? 'depth' : 'time',
                span: originalSpan
                    ? {
                          begin: originalSpan.start,
                          end: originalSpan.end
                      }
                    : undefined,
                start: event.events,
                items: [],
                buffer: []
            }
        case 'span':
            // don't update span for depth chart.
            if (event.baseline || state.type === 'depth') return state

            return cropMessages({
                ...state,
                // update span removing the state.extended to render this chart correctly
                span: { begin: event.events.begin, end: event.events.end - state.extended }
            })

        case 'endHistory':
            if (event.baseline) return state

            return {
                ...state,
                items: state.buffer,
                buffer: undefined
            }
        case 'event':
            if (event.events.length === 0) {
                return state
            }
            const prop = state.buffer !== undefined ? 'buffer' : 'items'
            state[prop] = state[prop].concat(event.events)
            sort(state[prop], (a: DataEvent, b: DataEvent) => a.timestamp - b.timestamp)

            return cropMessages(state)
        default:
            return state
    }
}
